import React from 'react';
import CompactSelectPicker from '../ui/CompactSelectPicker';
import MemberSelect from '../ui/MemberSelect';
import Tooltip from '../ui/Tooltip';
import DatePicker from '../ui/DatePicker';
import TaskLabelPicker from './TaskLabelPicker';
import { STATUS_OPTIONS } from './TaskStatus';
import { PriorityBarsIcon } from './TaskPriorityBars';
import { taskStatusOf, statusPatch } from '../../extensions/bundled/task-manager/taskStatus';
import { toggleTaskLabel } from '../../extensions/bundled/task-manager/boardLabels';

export const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', leading: <PriorityBarsIcon priority="low" /> },
  { value: 'medium', label: 'Medium', leading: <PriorityBarsIcon priority="medium" /> },
  { value: 'high', label: 'High', leading: <PriorityBarsIcon priority="high" /> },
];

/**
 * The Labels dropdown (pick, or create a new one). The task's labels are not
 * repeated as chips in the row (owner, 06.10.2026): they show ticked in the
 * dropdown and as "Labels · n" on it; the board's cards and list rows still
 * show them.
 */
function LabelChips({ palette, held, task, onChange, onAddLabel }) {
  return (
    <>
      <TaskLabelPicker
        palette={palette}
        held={held}
        // Writes the whole array, because that is what rides in the record's
        // `data` — a patch of one id would need the sync layer to understand arrays.
        onToggle={(id) => onChange({ labels: toggleTaskLabel(task, id) })}
        onCreate={onAddLabel}
      />
    </>
  );
}

/**
 * The task's properties as one row of chips (redesign after the owner's mock,
 * 05.10.2026): status, priority, due date, and on team boards assignee and
 * owner; the labels. When it was created and last changed sits under the
 * title since 06.10.2026 (TaskModalHeader). Every chip is the app's own picker, styled as a
 * pill — the same choices as before, one click away.
 */
export default function TaskMetaChips({ task, isTeamBoard, members, currentUserId, assigneeValue, palette, heldLabels, onChange, onAddLabel = null }) {
  const status = taskStatusOf(task);
  return (
    <div className="task-chips">
      <CompactSelectPicker
        className={`task-chip task-chip--status task-chip--${status}`}
        value={status}
        onChange={(v) => onChange(statusPatch(v))}
        options={STATUS_OPTIONS}
        aria-label="Status"
      />
      <CompactSelectPicker
        className="task-chip"
        value={task.priority || 'medium'}
        onChange={(v) => onChange({ priority: v })}
        options={PRIORITY_OPTIONS}
        aria-label="Priority"
      />
      <DatePicker
        className={`task-chip task-chip--date${task.dueDate ? '' : ' task-chip--empty'}`}
        value={task.dueDate || ''}
        onChange={(v) => onChange({ dueDate: v || null })}
        placeholder="Due date"
        aria-label="Due date"
      />
      {/* Assignee is a team concept: a private board is a to-do list where
          every task is already yours, so the chip is not offered there. */}
      {/* Which person chip is which is said on hover, not by a caption in the
          row (owner, 06.10.2026: no "OWNER" text beside the pills). */}
      {isTeamBoard && (
        <Tooltip content="Assignee — who does the work" side="bottom">
          <span className="task-chip-group">
            <MemberSelect
              className="task-chip task-chip--person"
              value={assigneeValue || null}
              // MemberSelect writes `v || null` and never undefined: an undefined
              // field vanishes through JSON on the way into the synced record, so
              // unassigning would appear to work locally and never clear anywhere else.
              onChange={(v) => onChange({ assigneeId: v })}
              members={members}
              currentUserId={currentUserId}
              emptyLabel="Unassigned"
              aria-label="Assignee"
            />
          </span>
        </Tooltip>
      )}
      {/* Owner is who wants to KNOW, assignee is who has to DO. They are
          usually different people — the person who raised the task keeps
          caring about it after handing it over, and separating the two is
          what lets the owner be notified about progress without also
          appearing to be responsible for the work. Set to whoever created
          the task and changeable from here, so a task raised by the wrong
          person, or by someone who then leaves, can be handed on. */}
      {isTeamBoard && (
        <Tooltip content="Owner — who wants to know" side="bottom">
          <span className="task-chip-group">
            <MemberSelect
              className="task-chip task-chip--person"
              value={task.ownerId ?? null}
              onChange={(v) => onChange({ ownerId: v })}
              members={members}
              currentUserId={currentUserId}
              emptyLabel="No owner"
              aria-label="Owner"
            />
          </span>
        </Tooltip>
      )}
      <LabelChips palette={palette} held={heldLabels} task={task} onChange={onChange} onAddLabel={onAddLabel} />
    </div>
  );
}
